import { Injectable, signal } from '@angular/core';
import { T2dDeletionPreview } from '@tec2date/ui-components';
import { DmsLinkEntityType } from '../../types';

export type DmsDocumentDeleteMode = 'unlink' | 'delete';

export interface DmsDocumentDeleteRequest {
  /** Anzeigename des Dokuments (für Dialog-Titel/Confirm). */
  documentName: string;
  /** Angereicherte Lösch-Vorschau (Tree = konkrete verknüpfte Objekte). */
  preview: T2dDeletionPreview;
  /**
   * Konkreter Bezugstyp im Entitäts-Kontext. null = globale Liste / Detailseite:
   * dann bietet der Dialog NUR „Dokument löschen" (keine Modus-Wahl).
   */
  entityType: DmsLinkEntityType | null;
  /**
   * true, wenn die aktuelle Verknüpfung die letzte des Dokuments ist (DMS-V02).
   * Dann ist „Verknüpfung lösen" deaktiviert.
   */
  isLastLink: boolean;
}

/**
 * DMS-spezifischer Lösch-Wahl-Dialog (Spec §7.2/§7.3).
 *
 * Bewusste, dokumentierte Abweichung von „Lösch-Modals immer T2dEntityDeleteService":
 * die Link-only-vs-Dokument-Wahl ist DMS-Semantik. Optisch am T2dDeleteDialog-Tree-Look
 * orientiert. Promise-/Signal-Pattern analog T2dDeleteDialogService.
 *
 * Voraussetzung: <t2d-dms-document-delete-dialog-host /> einmalig im Default-Layout.
 *
 * ask() resolved zu:
 *  - 'unlink'  → nur Verknüpfung zum aktuellen Bezugstyp lösen
 *  - 'delete'  → Dokument inkl. aller Verknüpfungen soft-löschen
 *  - null      → Abbruch
 */
@Injectable({ providedIn: 'root' })
export class DmsDocumentDeleteService {
  private readonly _request = signal<DmsDocumentDeleteRequest | null>(null);
  readonly request = this._request.asReadonly();

  private resolver: ((value: DmsDocumentDeleteMode | null) => void) | null = null;

  ask(request: DmsDocumentDeleteRequest): Promise<DmsDocumentDeleteMode | null> {
    // Offenen Vorgänger-Dialog abbrechen.
    if (this.resolver) this.resolver(null);

    this._request.set(request);
    return new Promise<DmsDocumentDeleteMode | null>((resolve) => {
      this.resolver = resolve;
    });
  }

  resolve(value: DmsDocumentDeleteMode | null): void {
    const r = this.resolver;
    this.resolver = null;
    this._request.set(null);
    r?.(value);
  }
}
